import { useContext, useReducer } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import {
  getTallyCompanies,
  getTallyCompanyDetails,
  getTallyVendorDetails,
  getTallyVoucherRegisters,
  getTallyPurchaseAndSalesLedgers,
} from '../services/tally-service';
import {
  TallyLedgerGroups,
  TallyVendor,
  TallyVouchersTypes,
  Fetch_Data_For_Vouchers,
} from '../../types/vendor';
import { useGetAllCompanyMasters } from '../query/companyMaster';
import TenantContext from '../context/TenantContext';
import {
  addEntity,
  getByPartitionKey,
  addEntitiesBulk,
  BulkEntityData,
} from '../services/api-service';
import { TableName } from '../../constants/table';
import { TallyCompany } from '../../types/tally';
import { TallyError } from '../errors/TallyError';
import * as XLSX from 'xlsx';
import { getCurrentFinancialYear } from '../utils/dateHelper';

interface TallyProgress {
  percentage: number;
  message: string;
  totalVendors?: number;
  processedVendors?: number;
  totalVoucherTypes?: number;
  processedVoucherTypes?: number;
}

interface TallyState {
  companies: TallyCompany[];
  loading: boolean;
  error: string | null;
  openConfirmDialog: boolean;
  selectedCompany: TallyCompany | null;
  openVendorDialog: boolean;
  currentVendors: TallyVendor[];
  fetchingVendors: boolean;
  vendorFetchError: string | null;
  progress: TallyProgress | null;
}

type TallyAction =
  | { type: 'FETCH_COMPANIES_START' }
  | { type: 'FETCH_COMPANIES_SUCCESS'; payload: TallyCompany[] }
  | { type: 'FETCH_COMPANIES_ERROR'; payload: string }
  | { type: 'SET_SELECTED_COMPANY'; payload: TallyCompany | null }
  | { type: 'SET_OPEN_CONFIRM_DIALOG'; payload: boolean }
  | { type: 'FETCH_VENDORS_START' }
  | { type: 'FETCH_VENDORS_SUCCESS'; payload: TallyVendor[] }
  | { type: 'FETCH_VENDORS_ERROR'; payload: string }
  | { type: 'SET_OPEN_VENDOR_DIALOG'; payload: boolean }
  | { type: 'UPDATE_COMPANY'; payload: TallyCompany }
  | { type: 'UPDATE_COMPANIES'; payload: TallyCompany[] }
  | { type: 'SET_PROGRESS'; payload: TallyProgress | null };

const initialState: TallyState = {
  companies: [],
  loading: false,
  error: null,
  openConfirmDialog: false,
  selectedCompany: null,
  openVendorDialog: false,
  currentVendors: [],
  fetchingVendors: false,
  vendorFetchError: null,
  progress: null,
};

const tallyReducer = (state: TallyState, action: TallyAction): TallyState => {
  switch (action.type) {
    case 'FETCH_COMPANIES_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_COMPANIES_SUCCESS':
      return { ...state, loading: false, companies: action.payload };
    case 'FETCH_COMPANIES_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'SET_SELECTED_COMPANY':
      return { ...state, selectedCompany: action.payload };
    case 'SET_OPEN_CONFIRM_DIALOG':
      return { ...state, openConfirmDialog: action.payload };
    case 'FETCH_VENDORS_START':
      return {
        ...state,
        fetchingVendors: true,
        vendorFetchError: null,
        progress: { percentage: 0, message: 'Starting vendor fetch...' },
      };
    case 'FETCH_VENDORS_SUCCESS':
      return {
        ...state,
        fetchingVendors: false,
        currentVendors: action.payload,
        progress: null,
      };
    case 'FETCH_VENDORS_ERROR':
      return {
        ...state,
        fetchingVendors: false,
        vendorFetchError: action.payload,
        progress: null,
      };
    case 'SET_OPEN_VENDOR_DIALOG':
      return { ...state, openVendorDialog: action.payload };
    case 'UPDATE_COMPANY':
      return {
        ...state,
        companies: state.companies.map(c =>
          c.name === action.payload.name ? action.payload : c,
        ),
      };
    case 'UPDATE_COMPANIES':
      return { ...state, companies: action.payload };
    case 'SET_PROGRESS':
      return {
        ...state,
        progress: { ...state.progress, ...action.payload } as TallyProgress,
      };
    default:
      return state;
  }
};

export const useTally = () => {
  const { tenant } = useContext(TenantContext);
  const { data: existingCompanies, refetch: refetchCompanies } =
    useGetAllCompanyMasters(tenant.id ?? '');

  const [state, dispatch] = useReducer(tallyReducer, initialState);

  const handleFetchCompanies = async () => {
    dispatch({ type: 'FETCH_COMPANIES_START' });
    try {
      const companyNames = await getTallyCompanies();
      const companies = companyNames.map(name => ({
        name,
        gstNumber: null,
        loading: false,
        isEditingGst: false,
        editedGstNumber: null,
        dbStatus: 'not_checked' as 'not_checked',
        detailsFetched: false,
        corruptedVendors: [],
      }));
      dispatch({ type: 'FETCH_COMPANIES_SUCCESS', payload: companies });
    } catch (err) {
      if (err instanceof TallyError) {
        dispatch({ type: 'FETCH_COMPANIES_ERROR', payload: err.message });
      } else {
        dispatch({
          type: 'FETCH_COMPANIES_ERROR',
          payload: 'An unknown error occurred.',
        });
      }
      console.error('Error fetching companies from Tally:', err);
    }
  };

  const fetchVoucherNamesForVendors = async (
    vendors: TallyVendor[],
  ): Promise<TallyVendor[]> => {
    const [
      purchaseAccountsLedgers,
      salesAccountsLedgers,
      directExpensesAccountsLedgers,
      indirectExpensesAccountsLedgers,
      indirectIncomesAccountsLedgers,
      directIncomesAccountsLedgers,
    ] = await Promise.all([
      getTallyPurchaseAndSalesLedgers(TallyLedgerGroups.purchaseAccount),
      getTallyPurchaseAndSalesLedgers(TallyLedgerGroups.salesAccount),
      getTallyPurchaseAndSalesLedgers(TallyLedgerGroups.directExpensesAccount),
      getTallyPurchaseAndSalesLedgers(TallyLedgerGroups.indirectExpesesAccount),
      getTallyPurchaseAndSalesLedgers(TallyLedgerGroups.directIncomesAccount),
      getTallyPurchaseAndSalesLedgers(TallyLedgerGroups.indirectIncomesAccount),
    ]);

    const voucherTypes = Fetch_Data_For_Vouchers;

    const totalVoucherTypes = voucherTypes.length;
    dispatch({
      type: 'SET_PROGRESS',
      payload: {
        percentage: 50,
        message: 'Fetching voucher types and ledgers...',
        totalVoucherTypes,
      },
    });

    const vendorInfoMap = new Map<
      string,
      { voucherName: string; chartOfAccount: string }
    >();

    const { fromDate, toDate } = getCurrentFinancialYear();

    for (let i = 0; i < totalVoucherTypes; i++) {
      const voucherType = voucherTypes[i];
      const progress = 50 + Math.round(((i + 1) / totalVoucherTypes) * 40);
      dispatch({
        type: 'SET_PROGRESS',
        payload: {
          percentage: progress,
          message: `Fetching ${voucherType} vouchers...`,
          processedVoucherTypes: i + 1,
        },
      });

      const voucherRegisters = await getTallyVoucherRegisters(
        voucherType,
        purchaseAccountsLedgers,
        salesAccountsLedgers,
        directExpensesAccountsLedgers,
        indirectExpensesAccountsLedgers,
        indirectIncomesAccountsLedgers,
        directIncomesAccountsLedgers,
        [], // journalAccountsLedgers - empty as journals can use any ledger
        fromDate,
        toDate,
      );

      for (const entry of voucherRegisters) {
        const existing = vendorInfoMap.get(entry.vendorName);
        if (!existing) {
          vendorInfoMap.set(entry.vendorName, {
            voucherName: entry.voucherName,
            chartOfAccount: entry.chartOfAccount,
          });
        } else if (
          existing.voucherName === TallyVouchersTypes.purchase &&
          entry.voucherName === TallyVouchersTypes.sales
        ) {
          vendorInfoMap.set(entry.vendorName, {
            voucherName: entry.voucherName,
            chartOfAccount: entry.chartOfAccount,
          });
        }
      }
    }

    dispatch({
      type: 'SET_PROGRESS',
      payload: { percentage: 95, message: 'Finalizing vendor data...' },
    });

    return vendors.map(vendor => {
      const info = vendorInfoMap.get(vendor.vendorName);
      return info ? { ...vendor, ...info } : vendor;
    });
  };

  const handleFetchVendorDetails = async (companyName: string) => {
    dispatch({ type: 'FETCH_VENDORS_START' });
    try {
      dispatch({
        type: 'SET_PROGRESS',
        payload: { percentage: 10, message: 'Fetching vendor names...' },
      });
      const { vendors, corruptedVendors } = await getTallyVendorDetails(
        companyName,
        progress => {
          dispatch({ type: 'SET_PROGRESS', payload: progress });
        },
      );

      if (corruptedVendors.length > 0) {
        toast.warn(
          `${corruptedVendors.length} corrupted vendor names found. You can download the list after closing this dialog.`,
        );
      }

      const updatedCompanies = state.companies.map(c =>
        c.name === companyName
          ? { ...c, corruptedVendors: corruptedVendors }
          : c,
      );
      dispatch({ type: 'UPDATE_COMPANIES', payload: updatedCompanies });

      dispatch({
        type: 'SET_PROGRESS',
        payload: {
          percentage: 40,
          message: 'Processing vendor details...',
        },
      });
      const vendorsWithVoucherNames =
        await fetchVoucherNamesForVendors(vendors);

      dispatch({
        type: 'SET_PROGRESS',
        payload: { percentage: 100, message: 'Done!' },
      });
      dispatch({
        type: 'FETCH_VENDORS_SUCCESS',
        payload: vendorsWithVoucherNames,
      });
      dispatch({ type: 'SET_OPEN_VENDOR_DIALOG', payload: true });
    } catch (err) {
      if (err instanceof TallyError) {
        dispatch({ type: 'FETCH_VENDORS_ERROR', payload: err.message });
      } else {
        dispatch({
          type: 'FETCH_VENDORS_ERROR',
          payload: 'An unknown error occurred.',
        });
      }
      console.error(`Error fetching vendor details:`, err);
    }
  };

  const handleCloseVendorDialog = () => {
    dispatch({ type: 'SET_OPEN_VENDOR_DIALOG', payload: false });
    dispatch({ type: 'FETCH_VENDORS_SUCCESS', payload: [] });
  };

  const handleFetchDetails = (company: TallyCompany) => {
    dispatch({ type: 'SET_SELECTED_COMPANY', payload: company });
    dispatch({ type: 'SET_OPEN_CONFIRM_DIALOG', payload: true });
  };

  const handleConfirmFetch = async () => {
    if (!state.selectedCompany) return;

    // Create a local constant to assert non-nullability
    const currentSelectedCompany = state.selectedCompany;

    dispatch({ type: 'SET_OPEN_CONFIRM_DIALOG', payload: false });

    dispatch({
      type: 'UPDATE_COMPANIES',
      payload: state.companies.map(c =>
        c.name === currentSelectedCompany.name
          ? {
              ...c,
              loading: true,
              dbStatus: c.dbStatus as 'not_checked' | 'present' | 'not_present',
            }
          : c,
      ),
    });

    try {
      const gstNumber = await getTallyCompanyDetails(
        currentSelectedCompany.name,
      );
      let dbStatus: 'not_checked' | 'present' | 'not_present' = 'not_present';
      let foundCompany = null;

      if (existingCompanies) {
        // First, check by GST number
        if (gstNumber) {
          foundCompany = existingCompanies.find(
            c => c.CompanyGst === gstNumber,
          );
        }

        // If not found by GST, check by CompanyOCR (Tally company name)
        if (!foundCompany) {
          foundCompany = existingCompanies.find(
            c => c.CompanyOCR === currentSelectedCompany.name,
          );
        }

        if (foundCompany) {
          dbStatus = 'present';
        }
      }

      if (gstNumber === null) {
        toast.warn('GST number not found for the active company in Tally.');
      }

      const finalCompanies = state.companies.map(company =>
        company.name === currentSelectedCompany.name
          ? {
              ...company,
              name: foundCompany ? foundCompany.CompanyName : company.name,
              gstNumber,
              loading: false,
              editedGstNumber: gstNumber,
              dbStatus,
              detailsFetched: true,
            }
          : company,
      );
      dispatch({ type: 'UPDATE_COMPANIES', payload: finalCompanies });
    } catch (err: any) {
      if (err instanceof TallyError) {
        toast.error(err.message);
      } else {
        toast.error('An unknown error occurred.');
      }
      console.error(
        `Error fetching details for ${currentSelectedCompany.name}:`,
        err,
      );
      const finalCompanies = state.companies.map(c =>
        c.name === currentSelectedCompany.name ? { ...c, loading: false } : c,
      );
      dispatch({ type: 'UPDATE_COMPANIES', payload: finalCompanies });
    }
    dispatch({ type: 'SET_SELECTED_COMPANY', payload: null });
  };

  const handleCloseConfirmDialog = () => {
    dispatch({ type: 'SET_OPEN_CONFIRM_DIALOG', payload: false });
    dispatch({ type: 'SET_SELECTED_COMPANY', payload: null });
  };

  const handleEditGst = (companyName: string) => {
    const updatedCompanies = state.companies.map(c =>
      c.name === companyName ? { ...c, isEditingGst: true } : c,
    );
    dispatch({ type: 'UPDATE_COMPANIES', payload: updatedCompanies });
  };

  const handleSaveGst = (companyName: string) => {
    const updatedCompanies = state.companies.map(c =>
      c.name === companyName
        ? { ...c, gstNumber: c.editedGstNumber, isEditingGst: false }
        : c,
    );
    dispatch({ type: 'UPDATE_COMPANIES', payload: updatedCompanies });
  };

  const handleCancelEditGst = (companyName: string) => {
    const updatedCompanies = state.companies.map(c =>
      c.name === companyName
        ? { ...c, editedGstNumber: c.gstNumber, isEditingGst: false }
        : c,
    );
    dispatch({ type: 'UPDATE_COMPANIES', payload: updatedCompanies });
  };

  const handleGstChange = (companyName: string, value: string) => {
    const updatedCompanies = state.companies.map(c =>
      c.name === companyName ? { ...c, editedGstNumber: value } : c,
    );
    dispatch({ type: 'UPDATE_COMPANIES', payload: updatedCompanies });
  };

  const handleAddCompany = async (company: TallyCompany) => {
    if (!tenant.id) {
      toast.error('Tenant ID not found.');
      return;
    }

    const partitionKey = tenant.id.toLowerCase();
    const companyMasterRowKey = company.gstNumber
      ? `GST_${company.gstNumber}`
      : uuid();
    const companyMappingRowKey = uuid();
    const isSystemGenerated = false;

    try {
      // Add to CompanyMaster
      await addEntity(TableName.CompanyMaster, {
        PartitionKey: partitionKey,
        RowKey: companyMasterRowKey,
        IsSystemGenerated: isSystemGenerated,
        Active: true,
        CompanyName: company.name,
        CompanyGst: company.gstNumber,
        ExportFormat: 1,
      });

      // Add to CompanyMapping
      await addEntity(TableName.CompanyMapping, {
        PartitionKey: partitionKey,
        RowKey: companyMappingRowKey,
        CompanyOCR: company.name,
        CompanyId: companyMasterRowKey,
        Active: true,
        IsSystemGenerated: isSystemGenerated,
      });

      toast.success(`Company '${company.name}' added successfully.`);
      refetchCompanies();
      const updatedCompanies = state.companies.map(c =>
        c.name === company.name
          ? { ...c, dbStatus: 'present' as 'present' }
          : c,
      );
      dispatch({ type: 'UPDATE_COMPANIES', payload: updatedCompanies });
    } catch (error) {
      toast.error(`Failed to add company '${company.name}'.`);
      console.error('Error adding company:', error);
    }
  };

  const handleSaveVendors = async (vendors: TallyVendor[]) => {
    if (!tenant.id) {
      toast.error('Tenant ID not found.');
      return;
    }

    const partitionKey = tenant.id.toLowerCase();

    // Fetch existing data
    const commonCode = await getByPartitionKey(
      TableName.CommonCode,
      tenant.id.toLowerCase(),
    );

    const commonCodeMapping = await getByPartitionKey(
      TableName.CommonCodeMapping,
      tenant.id.toLowerCase(),
    );

    // Collect entities for bulk operation - separate by table
    const commonCodeEntities: BulkEntityData[] = [];
    const commonCodeMappingEntities: BulkEntityData[] = [];
    const vendorOperations: Map<
      string,
      { type: 'add' | 'update' | 'error'; error?: string }
    > = new Map();

    for (const vendor of vendors) {
      const company = existingCompanies?.find(
        c => c.CompanyOCR === vendor.companyName,
      );
      if (!company) {
        vendorOperations.set(vendor.vendorName, {
          type: 'error',
          error: `Company '${vendor.companyName}' not found in the database.`,
        });
        continue;
      }

      const existingVendor = commonCode.find(
        (mapping: any) =>
          mapping.CompanyId === company.RowKey &&
          mapping.CommonCodeName === vendor.vendorName,
      );
      const existingVendorMapping = existingVendor
        ? commonCodeMapping.find(
            (mapping: any) =>
              mapping.CommonCodeId === existingVendor.RowKey &&
              mapping.CommonCodeOCR === vendor.vendorName,
          )
        : null;

      if (!existingVendor && !existingVendorMapping) {
        // Add new vendor - both entries don't exist
        const commonCodeUUID = uuid();
        const commonCodeMappingUUID = uuid();

        // Add CommonCode entity
        commonCodeEntities.push({
          partitionKey: partitionKey,
          rowKey: commonCodeUUID,
          entity: {
            CommonCode: vendor.vendorName,
            CommonCodeName: vendor.vendorName,
            ChartOfAccount: vendor.chartOfAccount,
            VoucherName: vendor.voucherName,
            TDSThreshold: vendor.tdsThreshold,
            TDSResidualAmount: vendor.tdsResidual,
            TDSRate: vendor.tdsRate,
            VendorGST: vendor.vendorGST,
            CompanyId: company.RowKey,
          },
          isUpdate: false,
        });

        // Add CommonCodeMapping entity
        commonCodeMappingEntities.push({
          partitionKey: partitionKey,
          rowKey: commonCodeMappingUUID,
          entity: {
            CommonCodeId: commonCodeUUID,
            CommonCodeOCR: vendor.vendorName,
          },
          isUpdate: false,
        });

        vendorOperations.set(vendor.vendorName, { type: 'add' });
      } else if (existingVendor && existingVendorMapping) {
        // Update existing vendor - both entries exist
        commonCodeEntities.push({
          partitionKey: existingVendor.PartitionKey,
          rowKey: existingVendor.RowKey,
          entity: {
            // CommonCodeName (OCR) is immutable - never update it
            CommonCodeName: existingVendor.CommonCodeName,
            CompanyId: existingVendor.CompanyId,
            CommonCode: vendor.vendorName,
            VendorGST: vendor.vendorGST ?? existingVendor.VendorGST,
            ChartOfAccount:
              vendor.chartOfAccount ?? existingVendor.ChartOfAccount,
            VoucherName: vendor.voucherName ?? existingVendor.VoucherName,
            TDSThreshold: vendor.tdsThreshold ?? existingVendor.TDSThreshold,
            TDSResidualAmount:
              vendor.tdsResidual ?? existingVendor.TDSResidualAmount,
            TDSRate: vendor.tdsRate ?? existingVendor.TDSRate,
          },
          isUpdate: true,
        });

        vendorOperations.set(vendor.vendorName, { type: 'update' });
      } else {
        // Data inconsistency - one exists but not the other
        vendorOperations.set(vendor.vendorName, {
          type: 'error',
          error: `Data inconsistency: ${existingVendor ? 'CommonCode exists but mapping is missing' : 'Mapping exists but CommonCode is missing'}. Please fix the data manually.`,
        });
      }
    }

    // Process pre-validation errors
    const preValidationErrors = Array.from(vendorOperations.entries()).filter(
      ([_, op]) => op.type === 'error',
    );
    preValidationErrors.forEach(([vendorName, op]) => {
      toast.error(`${vendorName}: ${op.error}`);
    });

    // If there are entities to process, make the bulk API calls
    if (commonCodeEntities.length > 0 || commonCodeMappingEntities.length > 0) {
      try {
        let totalSuccessCount = 0;
        let totalUpdateCount = 0;
        const allFailures: Array<{ vendorName: string; error: string }> = [];

        // Process CommonCode entities
        if (commonCodeEntities.length > 0) {
          const commonCodeResult = await addEntitiesBulk(
            TableName.CommonCode,
            commonCodeEntities,
          );
          totalSuccessCount += commonCodeResult.successCount;
          totalUpdateCount += commonCodeResult.updateCount;

          // Track failures from CommonCode
          commonCodeResult.failures.forEach(failure => {
            const vendorName =
              failure.entity.entity.CommonCodeName ||
              failure.entity.entity.CommonCode ||
              'Unknown vendor';
            allFailures.push({ vendorName, error: failure.error });
          });
        }

        // Process CommonCodeMapping entities
        if (commonCodeMappingEntities.length > 0) {
          const mappingResult = await addEntitiesBulk(
            TableName.CommonCodeMapping,
            commonCodeMappingEntities,
          );

          // Track failures from CommonCodeMapping
          mappingResult.failures.forEach(failure => {
            const vendorName =
              failure.entity.entity.CommonCodeOCR || 'Unknown vendor';
            allFailures.push({
              vendorName,
              error: `Mapping error: ${failure.error}`,
            });
          });
        }

        // Display success toast for added vendors
        if (totalSuccessCount > 0) {
          toast.success(
            `${totalSuccessCount} vendor${totalSuccessCount > 1 ? 's' : ''} added successfully.`,
          );
        }

        // Display info toast for updated vendors
        if (totalUpdateCount > 0) {
          toast.info(
            `${totalUpdateCount} vendor${totalUpdateCount > 1 ? 's' : ''} updated successfully.`,
          );
        }

        // Display error toasts for failures
        if (allFailures.length > 0) {
          allFailures.forEach(({ vendorName, error }) => {
            toast.error(`${vendorName}: ${error}`);
          });
        }
      } catch (error) {
        toast.error('Failed to process vendors. Please try again.');
        console.error('Error performing bulk vendor operation:', error);
      }
    } else if (preValidationErrors.length === 0) {
      toast.info('No vendors to process.');
    }
  };

  const handleDownloadCorruptedVendors = (companyName: string) => {
    const company = state.companies.find(c => c.name === companyName);
    if (
      company &&
      company.corruptedVendors &&
      company.corruptedVendors.length > 0
    ) {
      downloadCorruptedVendors(company.corruptedVendors);
    }
  };

  const downloadCorruptedVendors = (corruptedVendors: string[]) => {
    const worksheet = XLSX.utils.json_to_sheet(
      corruptedVendors.map(vendor => ({ 'Corrupted Vendor Name': vendor })),
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Corrupted Vendors');
    XLSX.writeFile(workbook, 'CorruptedVendorNames.xlsx');
  };

  return {
    state,
    dispatch,
    handleFetchCompanies,
    handleFetchVendorDetails,
    handleCloseVendorDialog,
    handleFetchDetails,
    handleConfirmFetch,
    handleCloseConfirmDialog,
    handleEditGst,
    handleSaveGst,
    handleCancelEditGst,
    handleGstChange,
    handleAddCompany,
    handleSaveVendors,
    handleDownloadCorruptedVendors,
  };
};
